import { isMsg, type Msg, type MsgParam } from "@/domain/msg";
import { MESSAGES, type Locale } from "./messages";

export type { Locale };
export const DEFAULT_LOCALE: Locale = "de";

const INTL: Record<Locale, string> = { de: "de-DE", en: "en-US" };

/** Number formats by language and decimals, made once: making one is costly, and the chart formats its price axis on every redraw. */
const NUMBER_FORMATS = new Map<string, Intl.NumberFormat>();
function numberFormat(tag: string, min: number, max: number): Intl.NumberFormat {
  const key = `${tag} ${min} ${max}`;
  const format = NUMBER_FORMATS.get(key) ?? new Intl.NumberFormat(tag, { minimumFractionDigits: min, maximumFractionDigits: max });
  NUMBER_FORMATS.set(key, format);
  return format;
}

/** Price decimals by magnitude: 2 from 10, 5 from 1 (currency pairs quote in pipettes, e.g. 1.17325), else 6. */
function priceDecimals(a: number): number {
  if (a >= 10) return 2;
  return a >= 1 ? 5 : 6;
}

/** A price rounded as prices are shown (its decimals by its size). */
export const roundPrice = (x: number) => Number(x.toFixed(priceDecimals(Math.abs(x))));

/** Money fraction digits: 1 when compact, 0 from 1000, else 2. */
function moneyDecimals(x: number, compact: boolean | undefined): number {
  if (compact) return 1;
  return Math.abs(x) >= 1000 ? 0 : 2;
}

/** Locale-aware formatters. All UI number/date output goes through these — times in `timeZone` (absent: the device's). */
function makeFormatters(locale: Locale, timeZone?: string) {
  const tag = INTL[locale];
  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const at = (t: number, o: Intl.DateTimeFormatOptions) => new Date(t).toLocaleString(tag, { ...o, timeZone: zone });
  const clockOf = (t: number) => at(t, { hour: "2-digit", minute: "2-digit" });
  const dayOf = (t: number) => at(t, { year: "numeric", month: "2-digit", day: "2-digit" });
  const fixed = (x: number, d: number) => numberFormat(tag, d, d).format(x);
  const price = (x: number | null | undefined): string => {
    if (x === null || x === undefined || !Number.isFinite(x)) return "—";
    const a = Math.abs(x);
    const d = priceDecimals(a);
    return numberFormat(tag, Math.min(2, d), d).format(x);
  };
  return {
    locale,
    tag,
    /** The time zone times are shown in (an IANA name). */
    zone,
    price,
    num(x: number | null | undefined, digits = 2): string {
      if (x === Infinity) return "∞";
      if (x === null || x === undefined || !Number.isFinite(x)) return "—";
      return numberFormat(tag, 0, digits).format(x);
    },
    fixed,
    pct(x: number | null | undefined, digits = 1, signed = false): string {
      if (x === null || x === undefined || !Number.isFinite(x)) return "—";
      const s = fixed(x * 100, digits);
      return `${signed && x > 0 ? "+" : ""}${s}${locale === "de" ? " %" : "%"}`;
    },
    money(x: number | null | undefined, currency = "USD", opts: { signed?: boolean; compact?: boolean } = {}): string {
      if (x === null || x === undefined || !Number.isFinite(x)) return "—";
      return new Intl.NumberFormat(tag, {
        style: "currency",
        currency: /^[A-Z]{3}$/.test(currency) ? currency : "USD",
        maximumFractionDigits: moneyDecimals(x, opts.compact),
        notation: opts.compact ? "compact" : "standard",
        signDisplay: opts.signed ? "exceptZero" : "auto",
      }).format(x);
    },
    /** Hour and minute in the shown time zone (e.g. when the running candle closes). */
    timeLocal(t: number | null | undefined): string {
      return t ? clockOf(t) : "—";
    },
    /** Short time in the shown time zone: the time today, else with the weekday ("Mo. 14:30"). */
    whenLocal(t: number | null | undefined, now = Date.now()): string {
      if (!t) return "—";
      return dayOf(t) === dayOf(now) ? clockOf(t) : at(t, { weekday: "short", hour: "2-digit", minute: "2-digit" });
    },
    /** Day, month and time in the shown time zone (e.g. when a position was opened). */
    dateTimeLocal(t: number | null | undefined): string {
      return t ? at(t, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
    },
    /** Day and month in the shown time zone ("28.10."), the date column of a calendar. */
    dayLocal(t: number | null | undefined): string {
      return t ? at(t, { day: "2-digit", month: "2-digit" }) : "—";
    },
    /** The day in the shown time zone with a two-digit year ("28.10.26"), for narrow tables. */
    dateShort(t: number | null | undefined): string {
      return t ? at(t, { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
    },
    date(t: number | null | undefined, withTime = false): string {
      if (!t) return "—";
      return new Date(t).toLocaleDateString(tag, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) });
    },
    ago(t: number | null | undefined): string {
      if (!t) return locale === "de" ? "nie" : "never";
      const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
      const s = Math.round((t - Date.now()) / 1000);
      if (Math.abs(s) < 60) return rtf.format(0, "second");
      if (Math.abs(s) < 3600) return rtf.format(Math.round(s / 60), "minute");
      if (Math.abs(s) < 86400) return rtf.format(Math.round(s / 3600), "hour");
      return rtf.format(Math.round(s / 86400), "day");
    },
  };
}

type Formatters = ReturnType<typeof makeFormatters>;

interface Translator {
  locale: Locale;
  f: Formatters;
  /** Translate a key with {placeholders}. Falls back to `fallback`, then English, then the key. */
  t(key: string, params?: Record<string, MsgParam>, fallback?: string): string;
  /** Render a domain message. */
  tm(m: Msg | null | undefined): string;
  /** True when the key exists in the active or English dictionary. */
  has(key: string): boolean;
}

/** A translator for `locale`, its times in `timeZone` (absent: the device's). */
export function createTranslator(locale: Locale, timeZone?: string): Translator {
  const dict = MESSAGES[locale] ?? MESSAGES[DEFAULT_LOCALE];
  const en = MESSAGES.en;
  const f = makeFormatters(locale, timeZone);

  const param = (v: MsgParam): string => {
    if (typeof v === "string") return v;
    if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString(f.tag) : f.num(v, 2);
    if (isMsg(v)) return tm(v);
    if (v === null) return "";
    if ("date" in v) return f.date(v.date);
    return Number.isFinite(v.n) ? f.pct(v.n, v.d, v.sign) : "—";
  };

  function t(key: string, params?: Record<string, MsgParam>, fallback?: string): string {
    const template = dict[key] ?? (fallback ?? (params?.fallback as string | undefined)) ?? en[key] ?? key;
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (_, name: string) => (params[name] === undefined ? `{${name}}` : param(params[name])));
  }

  function tm(m: Msg | null | undefined): string {
    if (m === null || m === undefined) return "";
    return t(m.k, m.p);
  }

  return { locale, f, t, tm, has: (k) => k in dict || k in en };
}

export interface I18n extends Translator {
  /** Localised instrument display name. */
  instrument(id: string, fallback: string): string;
}

export function makeI18n(t: Translator): I18n {
  return {
    ...t,
    instrument: (id, fallback) => t.t(`inst.${id}`, undefined, fallback),
  };
}

